import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import { Checkbox, Group, SegmentedControl, Text } from '@mantine/core';
import { neutralColors, semanticColors } from '@styles/design-system';
import { today as localToday } from '@utils/productionWindow';
import { type FC, useMemo } from 'react';

import { batchLabel, type ProductGroup, queueSplit } from './shipmentRows';
import { type DispatchView } from './useDispatchSelection';
import { formatCount } from '@utils/currency';

/** dd MMM yyyy, or a dash — the register's own format, so the two views read alike. */
function formatDate(value: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isFinite(date.getTime())
        ? date.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
          })
        : value;
}

/** Muted at zero, because a zero here is an ordinary answer rather than a gap. */
const quantityCell = (value: number | null, color?: string) => {
    if (value === null) return null;
    return value > 0 ? (
        <Text fz={13} c={color ?? neutralColors.textPrimary}>
            {formatCount(value)}
        </Text>
    ) : (
        <Text fz={13} c={neutralColors.textMuted}>
            0
        </Text>
    );
};

/**
 * A product, or one of the batches behind it.
 *
 * ONE TYPE FOR BOTH, which is what `getSubRows` requires and what every other expanding table
 * here already does — see `SiteProductionRow`'s order/line pair. The `kind` decides what each
 * column means rather than each column guessing from which fields happen to be set.
 */
interface DispatchRow {
    key: string;
    kind: 'product' | 'batch';
    /** The product name on a product row; the batch code on a batch. */
    label: string;
    /** Blank on a product; when the batch was made, on a batch. */
    detail: string | null;
    /** How many batches a product has. Null on a batch, which is one. */
    batchCount: number | null;
    available: number;
    approved: number;
    pending: number;
    expiry: string | null;
    /** Present on product rows only: what the tick acts on. */
    group?: ProductGroup;
    subRows?: DispatchRow[];
}

/**
 * The rows the grid renders: a product, and the batches it will actually draw on.
 *
 * THE CHILDREN SUM TO THE PARENT, by construction — both are read off the same
 * `ProductGroup.batches`, which is the dispatch queue filtered to what can still ship. A panel
 * reading the stock tables instead would have shown blocked and expired batches too, and left
 * somebody reconciling "2 batches" against a drawer listing four.
 *
 * IN FEFO ORDER, so what an expanded row shows is the order the shipment will consume them in.
 */
function buildRows(groups: ProductGroup[], today: string): DispatchRow[] {
    return groups.map((group) => ({
        key: `product:${group.itemId}`,
        kind: 'product' as const,
        label: group.itemName,
        detail: null,
        batchCount: group.batches.length,
        available: group.available,
        approved: group.approved,
        pending: group.pending,
        expiry: group.earliestExpiry,
        group,
        subRows: group.batches.map((batch) => {
            const split = queueSplit(batch, today);
            return {
                key: `batch:${batch.batchId}`,
                kind: 'batch' as const,
                // "No batch" for bucket stock, which has no code to show.
                label: batchLabel(batch),
                detail: batch.manufacturingDate,
                batchCount: null,
                available: batch.remaining,
                approved: split.approved,
                pending: split.pending,
                expiry: batch.expiryDate,
            };
        }),
    }));
}

/** The control that swaps the two views, rendered by whichever list is on screen. */
export const DispatchViewToggle: FC<{
    view: DispatchView;
    onChange: (view: DispatchView) => void;
}> = ({ view, onChange }) => (
    <SegmentedControl
        size="xs"
        value={view}
        onChange={(value) => onChange(value as DispatchView)}
        /*
         * "By product", not "FG". The tab strip two inches to the left already has a Batches
         * tab, and two controls reading "Batches" that do different things is the kind of
         * collision somebody only notices after clicking the wrong one.
         */
        data={[
            { value: 'product', label: 'By product' },
            { value: 'batch', label: 'By batch' },
        ]}
    />
);

interface ProductDispatchProps {
    groups: ProductGroup[];
    isLoading?: boolean;
    title?: string;
    subtitle?: string;
    tabs?: Array<{ id: string; label: string }>;
    activeTab?: string;
    onTabChange?: (tab: string) => void;
    view: DispatchView;
    onViewChange: (view: DispatchView) => void;
    isPicked: (group: ProductGroup) => boolean;
    onToggle: (group: ProductGroup) => void;
    onToggleAll: (groups: ProductGroup[]) => void;
}

/**
 * The dispatch queue at the grain people are asked to ship in.
 *
 * THE SAME ROWS AS THE BATCH VIEW, ADDED UP. Nothing here is fetched: `groupQueueByProduct`
 * folds the queue already on the page, so the two views cannot disagree about what is on the
 * shelf. Seven batches become five products, and a code that names two different items - which
 * happens, because a code is only unique to whoever printed it - stops being something the
 * person sending fifty tops has to know about.
 *
 * EXPANDS TO THE BATCHES, because "2 batches" answers how many and not which. They are the
 * eligible ones and only those, in the order the shipment will draw on them.
 *
 * THE TICK IS ON THE PRODUCT ONLY. Picking is the decision "this is going"; which batches cover
 * it is the shipment builder's question, where the quantities are. A second set of checkboxes
 * down here would be the same choice offered twice, with no way to tell which one the shipment
 * took.
 *
 * QC IS SPLIT RATHER THAN SUMMARISED. Unruled stock may travel - the brand inspects the load on
 * arrival - but somebody sending 96 jerseys none of QC has seen should know that before they
 * send them, not after.
 */
const ProductDispatch: FC<ProductDispatchProps> = ({
    groups,
    isLoading,
    title,
    subtitle,
    tabs,
    activeTab,
    onTabChange,
    view,
    onViewChange,
    isPicked,
    onToggle,
    onToggleAll,
}) => {
    const allPicked = groups.length > 0 && groups.every(isPicked);
    const rows = useMemo(() => buildRows(groups, localToday()), [groups]);

    const columns: DataSheetColumn<DispatchRow>[] = [
        {
            key: '_pick',
            header: '',
            width: 48,
            sticky: true,
            sortable: false,
            filterable: false,
            csvValue: () => '',
            // Partly-ticked shows as indeterminate rather than off: a product whose batches
            // were picked one by one in the other view is neither, and saying "off" would
            // invite a click that silently cleared them.
            renderHeader: () => (
                <Checkbox
                    size="xs"
                    aria-label="Send every product listed"
                    checked={allPicked}
                    indeterminate={!allPicked && groups.some(isPicked)}
                    disabled={groups.length === 0}
                    onChange={() => onToggleAll(groups)}
                />
            ),
            render: (_value: unknown, row: DispatchRow) =>
                row.kind === 'product' && row.group ? (
                    <Checkbox
                        size="xs"
                        aria-label={`Put ${row.label} on the shipment`}
                        checked={isPicked(row.group)}
                        onChange={() => row.group && onToggle(row.group)}
                        onClick={(event) => event.stopPropagation()}
                    />
                ) : null,
        },
        {
            key: 'label',
            header: 'Item',
            width: 300,
            sticky: true,
            render: (_value: unknown, row: DispatchRow) =>
                row.kind === 'product' ? (
                    <Text fz={13} fw={500} c={neutralColors.textPrimary}>
                        {row.label}
                    </Text>
                ) : (
                    // The code and the day it was made, which is what tells two runs of one
                    // product apart on a shelf.
                    <Group gap={8} wrap="nowrap">
                        <Text fz={13} c={neutralColors.textPrimary}>
                            {row.label}
                        </Text>
                        <Text fz={12} c={neutralColors.textMuted}>
                            {row.detail ? formatDate(row.detail) : 'no date'}
                        </Text>
                    </Group>
                ),
            csvValue: (_value: unknown, row: DispatchRow) => row.label,
        },
        {
            key: 'batchCount',
            header: 'Batches',
            width: 100,
            align: 'right',
            render: (_value: unknown, row: DispatchRow) => quantityCell(row.batchCount),
            csvValue: (_value: unknown, row: DispatchRow) =>
                row.batchCount === null ? '' : String(row.batchCount),
        },
        {
            key: 'available',
            header: 'Available',
            width: 120,
            align: 'right',
            render: (_value: unknown, row: DispatchRow) => quantityCell(row.available),
            csvValue: (_value: unknown, row: DispatchRow) => String(row.available),
        },
        {
            key: 'approved',
            header: 'QC Approved',
            width: 130,
            align: 'right',
            render: (_value: unknown, row: DispatchRow) =>
                quantityCell(row.approved, semanticColors.positive.dark),
            csvValue: (_value: unknown, row: DispatchRow) => String(row.approved),
        },
        {
            key: 'pending',
            header: 'Awaiting QC',
            width: 130,
            align: 'right',
            render: (_value: unknown, row: DispatchRow) =>
                quantityCell(row.pending, semanticColors.warning.dark),
            csvValue: (_value: unknown, row: DispatchRow) => String(row.pending),
        },
        {
            key: 'expiry',
            header: 'Earliest Expiry',
            width: 140,
            render: (_value: unknown, row: DispatchRow) => formatDate(row.expiry),
            csvValue: (_value: unknown, row: DispatchRow) => row.expiry ?? '',
        },
    ];

    return (
        <DataSheet<DispatchRow>
            data={rows}
            columns={columns}
            title={title ?? 'Dispatch'}
            subtitle={subtitle}
            tabs={tabs?.map((tab) => ({ id: tab.id, label: tab.label }))}
            activeTab={activeTab}
            onTabChange={onTabChange}
            isLoading={isLoading}
            enableSearch
            searchPlaceholder="Item…"
            enableDownload
            downloadFilename="dispatch-by-product"
            getRowKey={(row) => row.key}
            getSubRows={(row) => row.subRows}
            toolbarControls={
                <Group gap={8} wrap="nowrap">
                    <DispatchViewToggle view={view} onChange={onViewChange} />
                </Group>
            }
        />
    );
};

export default ProductDispatch;
